import { useEffect, useState } from "react";
import { FollowApiRoutes } from "../../../../shared/api-routes/member/follow-api.routes";
import http from "../../../../shared/utils/http";
import "../ActionButton.scss";
import "./FollowButton.scss";

export interface FollowButtonProps {
	follow_id: string;
	onFollow?: any;
	onUnfollow?: any;
	followed?: boolean;
	size: string;
}

// Global event emitter for follow/unfollow events
const followEventListeners = new Map<string, Set<(followed: boolean) => void>>();

const emitFollowEvent = (userId: string, followed: boolean) => {
	const listeners = followEventListeners.get(userId);
	if (listeners) {
		listeners.forEach((listener) => listener(followed));
	}
};

const subscribeToFollowEvents = (userId: string, callback: (followed: boolean) => void) => {
	if (!followEventListeners.has(userId)) {
		followEventListeners.set(userId, new Set());
	}
	followEventListeners.get(userId)!.add(callback);

	return () => {
		followEventListeners.get(userId)?.delete(callback);
	};
};

const FollowButton: React.FC<FollowButtonProps> = ({
	follow_id,
	onFollow,
	onUnfollow,
	followed,
	size,
}) => {
	const [isFollowed, setIsFollowed] = useState<boolean>(followed || false);

	// Sync internal state with prop changes
	useEffect(() => {
		setIsFollowed(followed || false);
	}, [followed]);

	// Subscribe to follow events for this user
	useEffect(() => {
		const unsubscribe = subscribeToFollowEvents(follow_id, (followed) => {
			setIsFollowed(followed);
		});

		return unsubscribe;
	}, [follow_id]);

	const followUnfollow = () => {
		if (isFollowed) {
			http
				.delete(FollowApiRoutes.FOLLOW_USER(follow_id))
				.then((res) => {
					onUnfollow && onUnfollow();
				})
				.catch((error) => {})
				.finally(() => {
					setIsFollowed(false);
					emitFollowEvent(follow_id, false);
				});
		} else
			http
				.post(FollowApiRoutes.FOLLOW_USER(follow_id))
				.then((res) => {
					if (res === "user_follow_success") {
						onFollow && onFollow();
					}
				})
				.catch((error) => {})
				.finally(() => {
					setIsFollowed(true);
					emitFollowEvent(follow_id, true);
				});
	};
	return (
		<div
			onClick={followUnfollow}
			className={`action-btn-${size} follow-btn ${
				isFollowed ? "unfollow" : "follow"
			}`}
		>
			<p className="text">{isFollowed ? "Following" : "+ Follow"}</p>
		</div>
	);
};

export default FollowButton;
